import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import withAuth from '../../../HOC/withAuth';
import InfoBar from '../InfoBar';
import Empty from '../../../components/Empty';
import CustomPagination from '../../../components/Pagination';
import C from '../../../colors';
import http from '../../../http';
import { getSmsPurposeLabel, getSmsStatusMeta, normalizeSmsLogsResponse } from '../../../smsLogView';

const STATUS_STYLES = {
  success: { background: C.successBg, color: C.successFg },
  warning: { background: C.warningBg, color: C.warningFg },
  danger: { background: C.dangerBg, color: C.dangerFg },
  neutral: { background: C.neutralBg, color: C.neutralFg },
};

function SmsLogsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ logs: [], total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError('');
    return http.get('/user/sms/logs', { params: { page, limit: 20 } })
      .then(response => setData(normalizeSmsLogsResponse(response.data)))
      .catch(() => {
        setData(current => ({ ...current, logs: [] }));
        setError('短信发送记录加载失败，请稍后重试。');
      })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <InfoBar />
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 36px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, color: C.brand2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>
              SMS Delivery
            </div>
            <h1 style={{ margin: 0, fontFamily: C.serif, fontSize: 36, fontWeight: 400, color: C.ink }}>短信发送记录</h1>
            <p style={{ margin: '8px 0 0', fontFamily: C.sans, fontSize: 13, lineHeight: 1.6, color: C.ink3 }}>
              查看短信通知的提交状态、接收号码和失败原因。“已受理”表示服务商已接收请求，最终送达仍受运营商网络影响。
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigate('/user/info')} style={{
              padding: '9px 14px', borderRadius: 7, border: `1px solid ${C.hair2}`,
              background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 13, cursor: 'pointer',
            }}>通知设置</button>
            <button data-testid="sms-log-refresh" onClick={fetchLogs} disabled={loading} style={{
              padding: '9px 14px', borderRadius: 7, border: `1px solid ${C.hair2}`,
              background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 13,
              cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.65 : 1,
            }}>↻ {loading ? '加载中…' : '刷新'}</button>
          </div>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '12px 16px', marginBottom: 14, borderRadius: 10,
          background: C.paper2, border: `1px solid ${C.hair}`,
        }}>
          <span style={{ fontFamily: C.sans, fontSize: 12, color: C.ink3 }}>发送记录只展示当前账号的安全摘要，不包含验证码或短信模板参数。</span>
          <strong data-testid="sms-log-total" style={{ fontFamily: C.mono, fontSize: 12, color: C.ink, whiteSpace: 'nowrap' }}>共 {data.total} 条</strong>
        </div>

        {error && (
          <div role="alert" style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 9, background: C.dangerBg, color: C.dangerFg, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading && data.logs.length === 0 ? (
          <div style={{ padding: '72px 0', textAlign: 'center', color: C.ink3, fontSize: 13 }}>正在加载短信记录…</div>
        ) : data.logs.length === 0 ? (
          <Empty message="暂无短信发送记录" />
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14, overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', fontFamily: C.sans }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.hair}`, background: C.paper2 }}>
                  {['提交时间', '短信用途', '接收号码', '状态', '详情', '关联订单'].map(label => (
                    <th key={label} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: C.mono, fontSize: 10, letterSpacing: 1, color: C.ink3, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.logs.map((log, index) => {
                  const status = getSmsStatusMeta(log.status);
                  const statusStyle = STATUS_STYLES[status.tone] || STATUS_STYLES.neutral;
                  const detail = log.failureReason || log.providerCode || status.description;
                  return (
                    <tr key={log.id || index} data-testid={log.id ? `sms-log-${log.id}` : undefined} style={{ borderBottom: index < data.logs.length - 1 ? `1px solid ${C.hair}` : 'none' }}>
                      <td style={{ padding: '14px 16px', color: C.ink2, fontSize: 12, whiteSpace: 'nowrap' }}>{log.createdAt ? dayjs(log.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—'}</td>
                      <td style={{ padding: '14px 16px', color: C.ink, fontSize: 13 }}>{getSmsPurposeLabel(log.purpose)}</td>
                      <td style={{ padding: '14px 16px', color: C.ink2, fontFamily: C.mono, fontSize: 12, whiteSpace: 'nowrap' }}>{log.phoneMasked || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span title={status.description} style={{ display: 'inline-flex', padding: '4px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', ...statusStyle }}>{status.label}</span>
                      </td>
                      <td style={{ padding: '14px 16px', color: log.status === 'failed' ? C.dangerFg : C.ink3, fontSize: 11.5, lineHeight: 1.5, maxWidth: 300 }}>{detail}</td>
                      <td style={{ padding: '14px 16px', color: C.ink3, fontFamily: C.mono, fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.orderId || ''}>{log.orderId || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data.totalPages > 1 && (
          <div style={{ marginTop: 16 }}>
            <CustomPagination count={data.totalPages} page={page} onChange={(_, value) => setPage(value)} />
          </div>
        )}
      </div>
    </div>
  );
}

export { SmsLogsPage };
export default withAuth(SmsLogsPage);
